import { CriteriaOperator, CriteriaType } from '@@types/api.types';
import type { CriterionSelectDto } from '@@types/api.types';

type CriteriaValues =
  | string[]
  | boolean
  | { max?: number; min?: number; value?: number }
  | { greaterThan?: number; lessThan?: number; max?: number; min?: number };

const CRITERIA_TYPE_LABELS: Record<CriteriaType, string> = {
  [CriteriaType.CATEGORY]: 'Technology Category',
  [CriteriaType.CMMC_LEVEL]: 'Organization CMMC Level',
  [CriteriaType.COUNTRY]: 'Organization Country',
  [CriteriaType.DOMAINS]: 'Technology Domain',
  [CriteriaType.MRL_SCORE]: 'Technology MRL Score',
  [CriteriaType.ORGANIZATION_TYPE]: 'Organization Type',
  [CriteriaType.RESEARCH_VALIDATED]: 'Technology Research Validated',
  [CriteriaType.REVENUE_RANGE]: 'Organization Revenue Range',
  [CriteriaType.SUBCATEGORY]: 'Subcategory',
  [CriteriaType.SUBSYSTEMS]: 'Technology Subsystems',
  [CriteriaType.TRL_QUESTION]: 'Technology TRL Question',
  [CriteriaType.TRL_SCORE]: 'Technology TRL Score',
  [CriteriaType.TYPE]: 'Technology Type',
  [CriteriaType.USES]: 'Technology Use',
};

function formatCurrency(value: number): string {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value}`;
}

export function formatArrayValues(values: string[], operator: CriteriaOperator): string {
  if (values.length === 0) {
    return '';
  }

  if (values.length === 1) {
    return values[0];
  }

  const lastValue = values[values.length - 1];
  const otherValues = values.slice(0, -1).join(', ');

  switch (operator) {
    case CriteriaOperator.INCLUDES_ANY_OF:
    case CriteriaOperator.IS_ANY_OF:
      return `${otherValues} or ${lastValue}`;
    case CriteriaOperator.INCLUDES_ALL_OF:
      return `${otherValues} and ${lastValue}`;
    case CriteriaOperator.IS_NONE_OF:
    case CriteriaOperator.EXCLUDES:
      return `${otherValues} or ${lastValue}`;
    default:
      return values.join(', ');
  }
}

function formatNumericValue(params: {
  operator: CriteriaOperator;
  type: CriteriaType;
  values: { greaterThan?: number; lessThan?: number; max?: number; min?: number; value?: number };
}): string {
  const { operator, type, values } = params;
  const isRevenue = type === CriteriaType.REVENUE_RANGE;

  if (operator === CriteriaOperator.BETWEEN || operator === CriteriaOperator.RANGE) {
    const min = values.min ?? values.greaterThan;
    const max = values.max ?? values.lessThan;

    if (min !== undefined && max !== undefined) {
      if (isRevenue) {
        return `${formatCurrency(min)} - ${formatCurrency(max)}`;
      }
      return `${min}-${max}`;
    }
  }

  if (operator === CriteriaOperator.GREATER_THAN && values.value !== undefined) {
    return `> ${values.value}`;
  }

  if (operator === CriteriaOperator.LESS_THAN && values.value !== undefined) {
    return `< ${values.value}`;
  }

  if (operator === CriteriaOperator.EQUALS && values.value !== undefined) {
    return `${values.value}`;
  }

  return '';
}

export function formatCriteriaDescription(params: {
  criterion: CriterionSelectDto;
  formattedValues?: string;
}): string {
  const { criterion, formattedValues } = params;
  const { operator, type, values } = criterion;
  const typeLabel = CRITERIA_TYPE_LABELS[type];
  const criteriaValues = values as CriteriaValues;

  // Handle boolean values
  if (typeof criteriaValues === 'boolean') {
    if (type === CriteriaType.RESEARCH_VALIDATED) {
      return criteriaValues ? 'Has Research Validated' : 'Research is not Validated';
    }
    if (type === CriteriaType.SUBSYSTEMS) {
      return criteriaValues ? 'Has Subsystems' : "Don't have Subsystems";
    }
    return `${typeLabel}: ${criteriaValues ? 'Yes' : 'No'}`;
  }

  // Handle array values (string[])
  if (Array.isArray(criteriaValues)) {
    const displayValues = formattedValues ?? formatArrayValues(criteriaValues, operator);

    switch (operator) {
      case CriteriaOperator.IS:
      case CriteriaOperator.EQUALS:
        return `${typeLabel}: ${displayValues}`;
      case CriteriaOperator.IS_ANY_OF:
      case CriteriaOperator.INCLUDES_ANY_OF:
        return `${typeLabel}: ${displayValues}`;
      case CriteriaOperator.INCLUDES_ALL_OF:
        return `${typeLabel}: ${displayValues}`;
      case CriteriaOperator.IS_NONE_OF:
      case CriteriaOperator.EXCLUDES:
        return `${typeLabel}: Not ${displayValues}`;
      case CriteriaOperator.IS_NOT:
        return `${typeLabel}: Not ${displayValues}`;
      default:
        return `${typeLabel}: ${displayValues}`;
    }
  }

  // Handle numeric values
  if (typeof criteriaValues === 'object' && criteriaValues !== null) {
    const formattedValue = formatNumericValue({ operator, type, values: criteriaValues });
    if (formattedValue) {
      return `${typeLabel} ${formattedValue}`;
    }
  }

  return `${typeLabel}`;
}
